import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { navigationItems } from '../../constants/navigation';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-soft' 
          : 'bg-background/80 backdrop-blur-sm'
      }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-6 lg:px-8">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-3 group focus-ring rounded-lg"
            onClick={closeMenu}
          >
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center shadow-medium group-hover:shadow-large transition-all duration-300">
                <Icon 
                  name="Code2" 
                  size={20} 
                  color="white" 
                  className="group-hover:scale-110 transition-transform duration-300" 
                />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-success rounded-full animate-pulse-soft"></div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-primary group-hover:text-accent transition-colors duration-300">
                Arvin Z's
              </h1>
              <p className="text-xs text-text-secondary font-medium tracking-wide">
                Portfolio
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 focus-ring ${
                  isActivePath(item.path)
                    ? 'bg-accent text-accent-foreground shadow-soft'
                    : 'text-text-secondary hover:text-primary hover:bg-muted'
                }`}
              >
                <Icon 
                  name={item.icon} 
                  size={16} 
                  className={`transition-colors duration-300 ${
                    isActivePath(item.path) ? 'text-accent-foreground' : ''
                  }`}
                />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:block">
            <Button
              variant="default"
              size="sm"
              iconName="MessageCircle"
              iconPosition="left"
              iconSize={16}
              className="bg-accent hover:bg-hover text-accent-foreground shadow-soft hover:shadow-medium"
              onClick={() => navigate('/contact-opportunities')}
            >
              Let's Chat
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-lg text-text-secondary hover:text-primary hover:bg-muted transition-all duration-300 focus-ring"
            aria-label="Toggle navigation menu"
          >
            <Icon 
              name={isMenuOpen ? "X" : "Menu"} 
              size={24} 
              className="transition-transform duration-300"
            />
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        <div 
          className={`lg:hidden transition-all duration-300 ease-smooth ${
            isMenuOpen 
              ? 'max-h-96 opacity-100 border-b border-border' :'max-h-0 opacity-0 overflow-hidden'
          }`}
        >
          <nav className="px-6 py-4 bg-background/95 backdrop-blur-md">
            <div className="space-y-2">
              {navigationItems.map((item, index) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 focus-ring animate-fade-in-up ${
                    isActivePath(item.path)
                      ? 'bg-accent text-accent-foreground shadow-soft'
                      : 'text-text-secondary hover:text-primary hover:bg-muted'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <Icon 
                    name={item.icon} 
                    size={18} 
                    className={`transition-colors duration-300 ${
                      isActivePath(item.path) ? 'text-accent-foreground' : ''
                    }`}
                  />
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>
            
            {/* Mobile CTA */}
            <div className="mt-4 pt-4 border-t border-border">
              <Button
                variant="default"
                size="sm"
                iconName="MessageCircle"
                iconPosition="left"
                iconSize={16}
                fullWidth
                className="bg-accent hover:bg-hover text-accent-foreground shadow-soft"
                onClick={() => {
                  closeMenu();
                  navigate('/contact-opportunities');
                }}
              >
                Start a Conversation
              </Button>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;